import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowLeft, Loader2, RefreshCw, Send } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { eventsApi } from '../../services/api';
import { ZoomPreview, ZoomPreviewSend, ZoomQueueSendResult } from '../../types';
import {
  applyZoomSpotlight,
  htmlHasZoomSpotlightSlot,
  sanitizeSpotlightLinkUrl,
} from '../../lib/zoomSpotlight';
import { toEmailPreviewSrcDoc } from './eventBuildUtils';
import { getApiErrorParts, logSafeAxiosError } from '../../utils/safeAxiosLog';

interface Props {
  onBack: () => void;
}

function formatYmd(ymd: string): string {
  const [y, m, d] = ymd.split('-').map(Number);
  if (!y || !m || !d) return ymd;
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

const ZoomEmailsPanel: React.FC<Props> = ({ onBack }) => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState<ZoomPreview | null>(null);
  const [sends, setSends] = useState<ZoomPreviewSend[]>([]);
  const [loadError, setLoadError] = useState<{ headline: string; detail?: string } | null>(null);

  const [previewEmails, setPreviewEmails] = useState<string[]>([]);
  const [selectedPreviewEmail, setSelectedPreviewEmail] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  const [queuing, setQueuing] = useState(false);
  const [queueResults, setQueueResults] = useState<ZoomQueueSendResult[] | null>(null);
  const [queueMessage, setQueueMessage] = useState<string | null>(null);

  const [spotlightEnabled, setSpotlightEnabled] = useState(false);
  const [spotlightMessage, setSpotlightMessage] = useState('');
  const [spotlightImageUrl, setSpotlightImageUrl] = useState('');
  const [spotlightLinkUrl, setSpotlightLinkUrl] = useState('');

  const loadPreview = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    setQueueResults(null);
    setQueueMessage(null);
    try {
      const res = await eventsApi.getZoomPreview();
      setPreview(res.data);
      setSends(res.data.sends);
    } catch (error) {
      logSafeAxiosError('Error loading Zoom preview', error);
      setPreview(null);
      setSends([]);
      setLoadError(getApiErrorParts(error, 'Failed to load Zoom Emails preview'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadPreview();
  }, [loadPreview]);

  useEffect(() => {
    axios
      .get<string[]>('/api/settings/preview-emails')
      .then((r) => {
        setPreviewEmails(r.data ?? []);
        if (r.data?.[0]) setSelectedPreviewEmail(r.data[0]);
      })
      .catch(() => {});
  }, []);

  const updateSend = (sendIndex: 1 | 2 | 3, patch: Partial<Pick<ZoomPreviewSend, 'date' | 'time'>>) => {
    setSends((prev) => prev.map((row) => (row.sendIndex === sendIndex ? { ...row, ...patch } : row)));
  };

  const classCounts = useMemo(() => {
    if (!preview) return { writing: 0, management: 0, sub: 0, skipped: 0 };
    return {
      writing: preview.buckets.writing.length,
      management: preview.buckets.management.length,
      sub: preview.buckets.sub.length,
      skipped: preview.skipped.length,
    };
  }, [preview]);

  const hasClasses = classCounts.writing + classCounts.management + classCounts.sub > 0;
  const segmentsReady = sends.length === 3 && sends.every((s) => s.segmentId != null);

  const hasSpotlightSlot = useMemo(
    () => (preview?.html ? htmlHasZoomSpotlightSlot(preview.html) : false),
    [preview?.html]
  );

  const assembledHtml = useMemo(() => {
    if (!preview?.html) return '';
    if (!spotlightEnabled) return applyZoomSpotlight(preview.html, null);
    return applyZoomSpotlight(preview.html, {
      message: spotlightMessage,
      imageUrl: spotlightImageUrl,
      linkUrl: spotlightLinkUrl,
    });
  }, [preview?.html, spotlightEnabled, spotlightMessage, spotlightImageUrl, spotlightLinkUrl]);

  const spotlightImageValid =
    !spotlightImageUrl.trim() || !!sanitizeSpotlightLinkUrl(spotlightImageUrl);
  const spotlightLinkValid =
    !spotlightLinkUrl.trim() || !!sanitizeSpotlightLinkUrl(spotlightLinkUrl);
  const spotlightImageSrc = sanitizeSpotlightLinkUrl(spotlightImageUrl);

  const canQueue =
    isAdmin && !!assembledHtml.trim() && hasClasses && segmentsReady && !loading && !queuing;
  const previewSrcDoc = useMemo(
    () => (assembledHtml ? toEmailPreviewSrcDoc(assembledHtml) : ''),
    [assembledHtml]
  );

  const sendTest = async () => {
    if (!preview) return;
    const to = selectedPreviewEmail.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(to)) {
      window.alert('Enter a valid test recipient email address.');
      return;
    }
    const html = assembledHtml.trim();
    if (!html) {
      window.alert('Nothing to send — the assembled Zoom email is empty.');
      return;
    }
    try {
      const pending = axios.post(
        '/api/email/preview-batch',
        {
          to,
          messages: [
            {
              subject: `[TEST] ${preview.subject}`.slice(0, 500),
              html: `${html}\n<!-- emailcraft-zoom-test ${Date.now()} -->`,
            },
          ],
        },
        { timeout: 120_000 }
      );
      setSendingTest(true);
      await pending;
      window.alert(`Sent a Zoom Emails test to ${to}.`);
    } catch (error) {
      logSafeAxiosError('Error sending Zoom test email', error);
      const detail = axios.isAxiosError(error)
        ? error.response?.data?.error || error.message
        : error instanceof Error
          ? error.message
          : 'Unknown error';
      window.alert(`Failed to send test email: ${detail}`);
    } finally {
      setSendingTest(false);
    }
  };

  const queueCampaigns = async () => {
    if (!canQueue) return;
    const ok = window.confirm(
      'Queue three identical Zoom emails to Zoom Send 1 (Tue), Zoom Send 2 (Wed), and Zoom Send 3 (Thu)?'
    );
    if (!ok) return;
    if (!preview) return;
    const payload = {
      subject: preview.subject,
      html: assembledHtml,
      sends: sends.map((s) => ({
        sendIndex: s.sendIndex,
        date: s.date,
        time: s.time,
        segmentId: s.segmentId as number,
      })),
    };
    const pending = eventsApi.queueZoomCampaigns(payload);
    setQueuing(true);
    setQueueResults(null);
    setQueueMessage(null);
    try {
      const res = await pending;
      setQueueResults(res.data.results);
      if (res.data.ok) {
        setQueueMessage('All three Zoom emails were queued in Interspire.');
      } else if (res.data.partial) {
        setQueueMessage(
          'Some Zoom emails queued and others failed. Do not click Queue again for the ones that already succeeded.'
        );
      } else {
        setQueueMessage(res.data.results.find((r) => r.error)?.error || 'Queue failed.');
      }
    } catch (error) {
      logSafeAxiosError('Error queuing Zoom campaigns', error);
      setQueueMessage(getApiErrorParts(error, 'Failed to queue Zoom campaigns').headline);
    } finally {
      setQueuing(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center text-sm font-medium text-gray-600 dark:text-slate-300 hover:text-blue-600 dark:hover:text-blue-400"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to event build
        </button>
        <button
          type="button"
          onClick={() => void loadPreview()}
          disabled={loading}
          className="inline-flex items-center px-3 py-1.5 text-sm border border-gray-300 dark:border-slate-600 rounded-md text-gray-700 dark:text-slate-200 bg-white dark:bg-slate-800 hover:bg-gray-50 dark:hover:bg-slate-700 disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
          Reload preview
        </button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-slate-400 py-12 justify-center">
          <Loader2 className="h-5 w-5 animate-spin" />
          Assembling Zoom emails…
        </div>
      ) : loadError ? (
        <div className="rounded-md border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-3 text-sm text-red-800 dark:text-red-200">
          <p className="font-medium">{loadError.headline}</p>
          {loadError.detail && <p className="mt-1 whitespace-pre-wrap opacity-90">{loadError.detail}</p>}
        </div>
      ) : preview ? (
        <>
          <div className="rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4">
            <p className="text-sm font-medium text-gray-900 dark:text-white">{preview.templateName}</p>
            <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">
              Subject: {preview.subject || '(empty)'}
            </p>
            <p className="text-xs text-gray-500 dark:text-slate-400 mt-2">
              Writing {classCounts.writing} · Management {classCounts.management} · Subawards {classCounts.sub}
              {classCounts.skipped > 0 ? ` · ${classCounts.skipped} Zoom event(s) skipped (unrecognized type)` : ''}
            </p>
            {(preview.errors.length > 0 || preview.warning) && (
              <ul className="mt-2 text-xs text-amber-800 dark:text-amber-200 list-disc pl-4 space-y-0.5">
                {preview.warning && <li>{preview.warning}</li>}
                {preview.errors.map((err) => (
                  <li key={err}>{err}</li>
                ))}
              </ul>
            )}
            {preview.skipped.length > 0 && (
              <ul className="mt-2 text-xs text-gray-500 dark:text-slate-400 list-disc pl-4">
                {preview.skipped.map((ev) => (
                  <li key={ev.id}>
                    Skipped: {ev.location || ev.id} ({[ev.type, ev.type_abbr].filter(Boolean).join(' / ') || 'no type'})
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4 space-y-3">
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={spotlightEnabled}
                onChange={(e) => setSpotlightEnabled(e.target.checked)}
                className="mt-0.5 rounded border-gray-300 text-blue-600"
              />
              <span>
                <span className="block text-sm font-medium text-gray-900 dark:text-white">
                  Add spotlight message / graphic
                </span>
                <span className="block text-xs text-gray-500 dark:text-slate-400 mt-0.5">
                  Place <code className="text-[11px]">{'{{ZOOM_SPOTLIGHT}}'}</code> in the Zoom template where this
                  block should appear (Email Templates → insert tag). Unused tags stay invisible. Without the tag,
                  we fall back to inserting before &quot;Grant Writing Workshops&quot;.
                </span>
              </span>
            </label>
            {spotlightEnabled && (
              <div className="space-y-3 pl-6 border-l-2 border-blue-200 dark:border-blue-900">
                {!hasSpotlightSlot && (
                  <p className="text-xs text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 rounded-md px-2.5 py-2">
                    This Zoom template has no <code className="text-[11px]">{'{{ZOOM_SPOTLIGHT}}'}</code> tag — add it
                    in Email Templates for exact placement. Using the workshops heading fallback for now.
                  </p>
                )}
                <label className="block">
                  <span className="text-xs font-medium text-gray-600 dark:text-slate-300">Message</span>
                  <textarea
                    value={spotlightMessage}
                    onChange={(e) => setSpotlightMessage(e.target.value)}
                    rows={3}
                    maxLength={2000}
                    placeholder="Optional short note…"
                    className="mt-1 block w-full rounded-md border-gray-300 dark:border-slate-600 dark:bg-slate-900 dark:text-white text-sm"
                  />
                </label>
                <label className="block">
                  <span className="text-xs font-medium text-gray-600 dark:text-slate-300">
                    Image URL <span className="font-normal text-gray-400">(optional)</span>
                  </span>
                  <input
                    type="url"
                    value={spotlightImageUrl}
                    onChange={(e) => setSpotlightImageUrl(e.target.value)}
                    placeholder="https://mailer.grantwritingusa.com/…"
                    className="mt-1 block w-full rounded-md border-gray-300 dark:border-slate-600 dark:bg-slate-900 dark:text-white text-sm"
                  />
                  <p className="mt-1 text-[11px] text-gray-400 dark:text-slate-500">
                    http(s) only. Upload in Interspire Image Manager, then paste the image URL here.
                  </p>
                  {!spotlightImageValid && (
                    <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                      Enter a full http:// or https:// image URL, or leave blank.
                    </p>
                  )}
                  {spotlightImageSrc && (
                    <img
                      src={spotlightImageSrc}
                      alt="Spotlight preview"
                      className="mt-2 max-h-40 max-w-full rounded border border-gray-200 dark:border-slate-600 object-contain"
                    />
                  )}
                </label>
                <label className="block">
                  <span className="text-xs font-medium text-gray-600 dark:text-slate-300">
                    Link URL <span className="font-normal text-gray-400">(optional)</span>
                  </span>
                  <input
                    type="url"
                    value={spotlightLinkUrl}
                    onChange={(e) => setSpotlightLinkUrl(e.target.value)}
                    placeholder="https://…"
                    className="mt-1 block w-full rounded-md border-gray-300 dark:border-slate-600 dark:bg-slate-900 dark:text-white text-sm"
                  />
                  <p className="mt-1 text-[11px] text-gray-400 dark:text-slate-500">
                    Click-through for the graphic (or message if no image). http(s) only.
                  </p>
                  {!spotlightLinkValid && (
                    <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                      Enter a full http:// or https:// URL, or leave blank.
                    </p>
                  )}
                </label>
              </div>
            )}
          </div>

          <div className="rounded-lg border border-gray-200 dark:border-slate-700 overflow-hidden bg-white dark:bg-slate-800">
            <div className="px-4 py-2 border-b border-gray-200 dark:border-slate-700">
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-slate-400">
                Preview — click each class URL to test it
              </p>
            </div>
            <iframe
              title="Zoom email preview"
              className="email-preview-frame w-full h-[min(70vh,40rem)] bg-white border-0"
              sandbox="allow-popups allow-popups-to-escape-sandbox allow-top-navigation-by-user-activation"
              srcDoc={previewSrcDoc}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {sends.map((row) => (
              <div
                key={row.sendIndex}
                className="rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4 space-y-3"
              >
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-blue-600 dark:text-blue-400">
                    {row.weekday}
                  </p>
                  <p className="text-sm font-medium text-gray-900 dark:text-white mt-0.5">{row.campaignName}</p>
                </div>
                <div>
                  <p className="text-[11px] font-medium text-gray-500 dark:text-slate-400 uppercase">Segment</p>
                  <p className="text-sm text-gray-900 dark:text-white mt-0.5">
                    {row.segmentName}
                    {row.segmentId != null ? (
                      <span className="text-xs text-gray-400 dark:text-slate-500"> #{row.segmentId}</span>
                    ) : (
                      <span className="text-xs text-red-600 dark:text-red-400"> — not found</span>
                    )}
                  </p>
                </div>
                <label className="block">
                  <span className="text-[11px] font-medium text-gray-500 dark:text-slate-400 uppercase">Date</span>
                  <input
                    type="date"
                    value={row.date}
                    onChange={(e) => updateSend(row.sendIndex, { date: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 dark:border-slate-600 dark:bg-slate-900 dark:text-white text-sm"
                  />
                  <span className="text-[11px] text-gray-400 dark:text-slate-500">{formatYmd(row.date)}</span>
                </label>
                <label className="block">
                  <span className="text-[11px] font-medium text-gray-500 dark:text-slate-400 uppercase">
                    Time (Pacific)
                  </span>
                  <input
                    type="time"
                    value={row.time}
                    onChange={(e) => updateSend(row.sendIndex, { time: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 dark:border-slate-600 dark:bg-slate-900 dark:text-white text-sm"
                  />
                </label>
              </div>
            ))}
          </div>

          <div className="flex flex-wrap items-end gap-3 rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4">
            <label className="flex-1 min-w-[12rem]">
              <span className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">Send test to</span>
              <input
                type="email"
                list="zoom-preview-emails"
                value={selectedPreviewEmail}
                onChange={(e) => setSelectedPreviewEmail(e.target.value)}
                className="block w-full rounded-md border-gray-300 dark:border-slate-600 dark:bg-slate-900 dark:text-white text-sm"
                placeholder="you@example.com"
              />
              <datalist id="zoom-preview-emails">
                {previewEmails.map((addr) => (
                  <option key={addr} value={addr} />
                ))}
              </datalist>
            </label>
            <button
              type="button"
              onClick={() => void sendTest()}
              disabled={sendingTest || !assembledHtml.trim()}
              className="inline-flex items-center px-4 py-2 rounded-md text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
            >
              {sendingTest ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Send className="h-4 w-4 mr-1.5" />}
              Send Test
            </button>
            {isAdmin && (
              <button
                type="button"
                onClick={() => void queueCampaigns()}
                disabled={!canQueue}
                className="inline-flex items-center px-4 py-2 rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
              >
                {queuing ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null}
                Queue Campaign
              </button>
            )}
          </div>

          {queueMessage && (
            <div className="rounded-md border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-900/40 p-3 text-sm text-gray-800 dark:text-slate-200">
              {queueMessage}
              {queueResults && (
                <ul className="mt-2 text-xs space-y-1">
                  {queueResults.map((r) => (
                    <li key={r.sendIndex}>
                      Send {r.sendIndex} → {r.segmentName}
                      {r.ok
                        ? ` queued${r.jobId != null ? ` (job ${r.jobId})` : ''}`
                        : ` failed: ${r.error || 'unknown error'}`}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </>
      ) : null}
    </div>
  );
};

export default ZoomEmailsPanel;
